import React, { useRef, useMemo, useEffect, useState } from "react";
import { Box, Typography, Button, IconButton, Dialog, DialogContent } from "@mui/material";
import ExamHeader from "./ExamHeader";
import { useQuestionSection } from "../../../hooks/default/useQuestionSection";
import { useLanguages } from "../../../context/LanguagesContext";
import ButtonSection from "./ButtonSection";
import SubmissionPopupReLogin from "../../../components/popups/SubmissionPopupReLogin";
import SubmissionPopup from "../../../components/popups/SubmissionPopup";
import ServerErrorDialog from "../../../components/popups/ServerErrorDialog";
import FinalSubmissionDialog from "../../../components/popups/FinalSubmissionDialog";
import useQuestionSectionStyles from "../testStyle/gateStyle/questionSectionStyle";
import { QuestionRenderer } from "../default/QuestionRenderer";
import Loader from "../../../components/Loader";
import down from "../../../images/down.png";
import up from "../../../images/up.png";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import Draggable from "react-draggable";

function QuestionSection({
  subjectNames = [],
  sectionNames = [],
  questionData = [],
  currentQuestion = 0,
  setCurrentQuestion,
  subjectId,
  currentSubject = "",
  currentSection = "",
  setActiveSubjectId,
  setActiveSectionId,
  activeSubjectId,
  activeSectionId,
  desktopOpen,
  setSscTimeUp,
  setIsOnSubmit,
  isMobile,
  saveBeforeSubmitRef,
  onTimerInitialized = () => { },
  isTimerInitialized = false,
}) {
  const { selectedLanguage } = useLanguages();
  const classes = useQuestionSectionStyles();
  const paperRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [selectedImg, setSelectedImg] = useState("");
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [touchStartDist, setTouchStartDist] = useState(null);
  const draggableRef = useRef(null);

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.5, 4));
  const handleZoomOut = () =>
    setScale((prev) => {
      const newScale = Math.max(prev - 0.5, 1);
      if (newScale === 1) setPosition({ x: 0, y: 0 });
      return newScale;
    });
  const handleResetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // Pinch-to-zoom logic
  const getDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      setTouchStartDist(getDistance(e.touches));
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && touchStartDist) {
      const dist = getDistance(e.touches);
      const ratio = dist / touchStartDist;
      setScale((prev) => Math.min(Math.max(prev * ratio, 1), 4));
      setTouchStartDist(dist);
    }
  };

  const handleTouchEnd = () => {
    setTouchStartDist(null);
  };

  // Mouse wheel zoom logic
  const handleWheel = (e) => {
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  // Double tap/click to toggle zoom
  const handleDoubleClick = () => {
    if (scale > 1) {
      handleResetZoom();
    } else {
      setScale(2);
    }
  };

  const handleImageClick = (e) => {
    if (e.target.tagName === "IMG") {
      setSelectedImg(e.target.src);
      setOpen(true);
    }
  };

  const filteredQuestions = useMemo(() => {
    return (
      questionData?.filter((q) => {
        try {
          return (
            q &&
            Number(q?.section_id) === Number(activeSectionId) &&
            Number(q?.subject_id) === Number(activeSubjectId)
          );
        } catch (error) {
          return false;
        }
      }) || []
    );
  }, [questionData, activeSectionId, activeSubjectId]);

  const question = useMemo(() => {
    if (!filteredQuestions || filteredQuestions.length === 0) {
      return null;
    }

    const q = filteredQuestions[currentQuestion];
    return q || null;
  }, [filteredQuestions, currentQuestion]);

  const {
    selectedOptionId,
    selectedOptionIds,
    inputAnswer,
    showSubmissionPopup,
    showSubmissionPopupReLogin,
    showServerErrorDialog,
    setShowServerErrorDialog,
    showSubmitModal,
    isTimeUp,
    countdownTime,
    finalSubmitChecked,
    submitError,
    countdownByHead,
    stats,
    handleOptionChange,
    handleMultipleChoiceChange,
    handleInputChange,
    resetSelections,
    handleSaveNextDefault,
    handleMarkReviewDefault,
    handleClearResponse,
    handleBack,
    handleNext,
    handleOpenSubmitModal,
    handleCloseSubmitModal,
    handleFinalSubmitCheck,
    handleFinalSubmit,
    handleSubmit,

    setMatrixAnswer,
    matrixAnswer,
    text,
    setText,
  } = useQuestionSection({
    questionData: filteredQuestions,
    currentQuestion,
    setCurrentQuestion,
    subjectId,
    activeSectionId,
    activeSubjectId,
    enableTimer: true,
    saveBeforeSubmitRef,
  });

  const handleQuestionChange = (index) => {
    if (index >= 0 && index < filteredQuestions.length) {
      setCurrentQuestion(index);
    }
  };

  const scrollToBottom = () => {
    if (paperRef.current) {
      paperRef.current.scrollTo({
        top: paperRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const scrollToTop = () => {
    if (paperRef.current) {
      paperRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    scrollToTop();
  }, [currentQuestion]);

  return (
    <Box className={classes.rootContainer}>
      {/* Fixed Header */}
      <Box className={classes.headerContainer}>
        <ExamHeader
          subjectNames={subjectNames}
          currentQuestion={currentQuestion}
          currentSubject={currentSubject}
          sectionNames={sectionNames}
          handleQuestionChange={handleQuestionChange}
          currentSection={currentSection}
          questionData={filteredQuestions}
          allQuestion={questionData}
          setCurrentQuestion={setCurrentQuestion}
          subjectId={subjectId}
          negativeMark={question?.negative_mark}
          mark={question?.mark}
          setActiveSubjectId={setActiveSubjectId}
          setActiveSectionId={setActiveSectionId}
          activeSubjectId={activeSubjectId}
          activeSectionId={activeSectionId}
          desktopOpen={desktopOpen}
          isMobile={isMobile}
          setSscTimeUp={setSscTimeUp}
          setIsOnSubmit={setIsOnSubmit}
          questionType={question?.question_type}
          saveBeforeSubmitRef={saveBeforeSubmitRef}
          onTimerInitialized={onTimerInitialized}
        />
      </Box>

      {/* Scrollable Content */}
      <Box className={classes.questionNoWrapper}>
        <Typography
          variant="body2"
          fontWeight={"bold"}
          className={classes.questionTitle}
        >
          Q. {currentQuestion + 1}:
        </Typography>
        <IconButton onClick={scrollToBottom} size="small">
          <img src={down} alt="Scroll down" style={{ width: 24, height: 24 }} />
        </IconButton>
      </Box>

      <Box
        className={`${classes.contentContainerMain} ${!desktopOpen || isMobile
          ? classes.contentContainerMainFull
          : classes.contentContainerMainHaf
          }`}
        style={{ display: "flex", flexDirection: isMobile ? "column" : "row", flex: 1, overflow: "hidden" }}
      >
        {(question?.paragraph_en || question?.paragraph_hi) && (
          <>
            <Box
              className={classes.contentContainer}
              sx={{
                width: isMobile ? "100%" : "50%",
                padding: "10px",
                borderRight: isMobile ? "none" : "2px solid #000",
                borderBottom: isMobile ? "2px solid #000" : "none",
                overflowY: "auto",
                overflowX: "hidden",

                // "& img": {
                //   // Targets all <img> inside this Box
                //   width: "100% !important",
                //   height: "auto !important",
                //   maxWidth: "100%",
                // },
              }}
            >
              <Typography
                variant="h6"
                style={{ paddingTop: "5px", paddingBottom: "5px" }}
              >
                Paragraph
              </Typography>
              <div
                className={classes.paragraphContainer}
                onClick={handleImageClick}
                dangerouslySetInnerHTML={{
                  __html:
                    selectedLanguage === "english"
                      ? question?.paragraph_en || ""
                      : question?.paragraph_hi || "",
                }}
              />
            </Box>
          </>
        )}
        <Box
          sx={{
            width:
              (question?.paragraph_en || question?.paragraph_hi)
                ? (isMobile ? "100%" : "50%")
                : "100%",
            pb: { xs: 1, md: 0 },
          }}
          ref={paperRef}
          className={classes.contentContainer}
        >
          {question && isTimerInitialized ? (
            <QuestionRenderer
              question={question}
              selectedLanguage={selectedLanguage}
              questionType={question?.question_type}
              selectedOptionId={selectedOptionId}
              selectedOptionIds={selectedOptionIds}
              inputAnswer={inputAnswer}
              handleOptionChange={handleOptionChange}
              handleMultipleChoiceChange={handleMultipleChoiceChange}
              handleInputChange={handleInputChange}
              classes={classes}
              setMatrixAnswer={setMatrixAnswer}
              matrixAnswer={matrixAnswer}
              text={text}
              setText={setText}
            />
          ) : (
            <>
              <Loader />
              {/* <Typography>Loading question or no question available</Typography> */}
            </>
          )}
        </Box>
      </Box>
      <div className={classes.upWrapper}>
        <IconButton onClick={scrollToTop} size="small">
          <img src={up} alt="Scroll up" style={{ width: 24, height: 24 }} />
        </IconButton>
      </div>
      <Box className={classes.buttonContainer}>
        <ButtonSection
          handleSaveNextDefault={handleSaveNextDefault}
          handleMarkReviewDefault={handleMarkReviewDefault}
          handleSubmit={handleOpenSubmitModal}
          handleClearResponse={handleClearResponse}
          currentQuestion={currentQuestion}
          question={question}
          questionData={filteredQuestions}
          handleBack={handleBack}
          handleNext={handleNext}
          setActiveSubjectId={setActiveSubjectId}
          setActiveSectionId={setActiveSectionId}
          activeSubjectId={activeSubjectId}
          activeSectionId={activeSectionId}
          subjectNames={subjectNames}
          sectionNames={sectionNames}
        />
      </Box>

      <SubmissionPopupReLogin
        open={showSubmissionPopupReLogin}
        countdown={countdownByHead}
      />

      <SubmissionPopup
        open={showSubmissionPopup}
        countdown={countdownByHead}
        stats={stats}
      />

      <ServerErrorDialog
        open={showServerErrorDialog}
        onClose={() => setShowServerErrorDialog(false)}
      />

      <FinalSubmissionDialog
        open={showSubmitModal || isTimeUp}
        onClose={handleCloseSubmitModal}
        onSubmit={handleFinalSubmit}
        isTimeUp={isTimeUp}
        stats={stats}
        submitError={submitError}
        countdownTime={countdownTime}
        finalSubmitChecked={finalSubmitChecked}
        onFinalSubmitCheck={handleFinalSubmitCheck}
      />

      {/* Premium Image Preview Modal */}
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          handleResetZoom();
        }}
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            width: { xs: "98vw", sm: "90vw", md: "80vw" },
            height: { xs: "70vh", sm: "80vh", md: "80vh" },
            maxHeight: "95vh",
            margin: { xs: "4px", md: "16px" },
            borderRadius: { xs: "12px", md: "8px" },
            // backgroundColor: "#1a1a1a",
            backgroundColor: "#fff",
            boxShadow: "0 24px 48px rgba(0,0,0,0.4)",
            overflow: "hidden",
          },
        }}
      >
        <IconButton
          onClick={() => {
            setOpen(false);
            handleResetZoom();
          }}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 1300,
            // color: "white",
            color: "black",
            // backgroundColor: "rgba(255,255,255,0.1)",
            backgroundColor: "rgba(0,0,0,0.05)",
            backdropFilter: "blur(4px)",
            "&:hover": {
              // backgroundColor: "rgba(255,255,255,0.2)"
              backgroundColor: "rgba(0,0,0,0.1)"
            },
            padding: "8px",
          }}
        >
          <CloseIcon fontSize="medium" />
        </IconButton>

        <DialogContent
          sx={{
            p: 0,
            // backgroundColor: "#111",
            backgroundColor: "#fff",
            overflow: "hidden",
            position: "relative",
            height: "100%",
            width: "100%",
          }}
        >
          <Draggable
            nodeRef={draggableRef}
            disabled={scale === 1}
            position={{ x: 0, y: 0 }}
            onDrag={(e, data) => setPosition(prev => ({ x: prev.x + data.deltaX, y: prev.y + data.deltaY }))}
          >
            <Box
              ref={draggableRef}
              onWheel={handleWheel}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onDoubleClick={handleDoubleClick}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: scale > 1 ? "grab" : "zoom-in",
                "&:active": { cursor: scale > 1 ? "grabbing" : "zoom-in" },
                transition: scale === 1 ? "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)" : "none",
                touchAction: scale > 1 ? "none" : "auto", // Disable native scroll when panning
                width: "100%",
                height: "100%",
              }}
            >
              <img
                src={selectedImg}
                alt="Preview"
                style={{
                  transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                  transition: scale === 1 ? "transform 0.3s ease" : "none",
                  maxWidth: "90%",
                  maxHeight: "90%",
                  objectFit: "contain",
                  userSelect: "none",
                  pointerEvents: "none",
                  // backgroundColor: "#fff", // Add white background to make transparent black text visible
                  // padding: "16px",
                  // borderRadius: "8px",
                }}
              />
            </Box>
          </Draggable>

          {/* Premium Zoom Controls ToolBar */}
          <Box
            sx={{
              position: "absolute",
              bottom: 24,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              // backgroundColor: "rgba(255,255,255,0.1)",
              backgroundColor: "rgba(0,0,0,0.8)",
              backdropFilter: "blur(12px)",
              padding: "6px 12px",
              borderRadius: "30px",
              // border: "1px solid rgba(255,255,255,0.1)",
              border: "1px solid rgba(0,0,0,0.1)",
              zIndex: 1200,
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            }}
          >
            <IconButton
              onClick={handleZoomOut}
              sx={{ color: "white", "&:disabled": { color: "rgba(255,255,255,0.3)" } }}
              disabled={scale <= 1}
              size="small"
            >
              <ZoomOutIcon />
            </IconButton>

            <Typography
              variant="caption"
              sx={{
                color: "white",
                minWidth: "45px",
                textAlign: "center",
                fontWeight: 600,
                fontSize: "0.75rem",
                letterSpacing: "0.5px"
              }}
            >
              {Math.round(scale * 100)}%
            </Typography>

            <IconButton
              onClick={handleZoomIn}
              sx={{ color: "white", "&:disabled": { color: "rgba(255,255,255,0.3)" } }}
              disabled={scale >= 4}
              size="small"
            >
              <ZoomInIcon />
            </IconButton>

            <Box sx={{ width: "1px", height: "16px", backgroundColor: "rgba(255,255,255,0.2)", mx: 1 }} />

            <IconButton
              onClick={handleResetZoom}
              sx={{ color: "white" }}
              size="small"
              title="Reset Zoom"
            >
              <RestartAltIcon />
            </IconButton>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default QuestionSection;
